import { useState, useEffect } from 'react';

const PROXY_URL = (import.meta.env.VITE_PROXY_URL || 'http://localhost:4005').replace(/\/+$/, '');

function TrendingCard({ item, onClick }) {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);

    return (
        <button
            onClick={onClick}
            className="flex-none rounded overflow-hidden relative cursor-pointer"
            style={{ width: 120, aspectRatio: '2/3', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
            {item.posterPath && !errored && (
                <img
                    src={item.posterPath}
                    alt={item.title}
                    className={`w-full h-full object-contain transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    onLoad={() => setLoaded(true)}
                    onError={() => setErrored(true)}
                />
            )}
            {loaded && !errored && item.posterPath && (
                <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 pointer-events-none">
                        <p className="text-white text-[11px] font-semibold font-body leading-tight line-clamp-2">{item.title}</p>
                        {item.year && (
                            <p className="text-white/50 text-[10px] mt-0.5">{item.year}</p>
                        )}
                    </div>
                </>
            )}
            {(!item.posterPath || errored) && (
                <div className="absolute inset-0 flex items-end p-2">
                    <span className="text-text-tertiary text-[10px] leading-tight line-clamp-3">{item.title}</span>
                </div>
            )}
        </button>
    );
}

export default function TrendingCarousel({ onItemClick, onDismiss }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${PROXY_URL}/api/bgg/trending?limit=30`)
            .then(r => r.ok ? r.json() : [])
            .then(data => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="px-4 sm:px-6 lg:px-8 py-6 border-t border-border-subtle">
                <div className="flex gap-3 overflow-hidden">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="flex-none rounded-xl bg-bg-tertiary animate-pulse" style={{ width: 120, aspectRatio: '2/3' }} />
                    ))}
                </div>
            </div>
        );
    }

    if (!items.length) return null;

    const loopItems = [...items, ...items];
    const speed = Math.max(40, items.length * 3);

    return (
        <>
            <style>{`
                @keyframes bg-carousel {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .bg-carousel-track {
                    display: flex;
                    gap: 12px;
                    width: max-content;
                    animation: bg-carousel ${speed}s linear infinite;
                }
                .bg-carousel-wrapper:hover .bg-carousel-track {
                    animation-play-state: paused;
                }
            `}</style>

            <div className="py-5">
                <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 mb-4">
                    <div>
                        <p className="text-text-tertiary text-xs">Suggestions to get you started</p>
                    </div>
                    <button
                        onClick={onDismiss}
                        className="text-text-tertiary hover:text-text-primary text-xs transition-colors duration-150 flex items-center gap-1"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Dismiss
                    </button>
                </div>

                <div className="overflow-hidden bg-carousel-wrapper px-4 sm:px-6 lg:px-8">
                    <div className="bg-carousel-track">
                        {loopItems.map((item, i) => (
                            <TrendingCard
                                key={`${item.bggId}-${i}`}
                                item={item}
                                onClick={() => onItemClick(item)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
